import { useEffect, RefObject } from 'react';

/**
 * Reserve space for a fixed banner by padding the body with the banner's real
 * rendered height, so page content is never hidden behind it.
 *
 * Measures the element rather than using a fixed value: banner content wraps at
 * narrow widths and the Early Bird countdown changes width as digits tick, so a
 * hardcoded height under-reserves on mobile and covers the page.
 */
export function useBodyOffset(
  ref: RefObject<HTMLElement | null>,
  side: 'top' | 'bottom',
  active: boolean
): void {
  useEffect(() => {
    const setPadding = (value: string) => {
      if (side === 'top') {
        document.body.style.paddingTop = value;
      } else {
        document.body.style.paddingBottom = value;
      }
    };

    const element = ref.current;
    if (!active || !element) {
      setPadding('');
      return;
    }

    const apply = () => setPadding(`${element.offsetHeight}px`);
    apply();

    // ResizeObserver catches wrapping and countdown width changes; fall back to
    // window resize where it isn't available.
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(apply);
      observer.observe(element);
      return () => {
        observer.disconnect();
        setPadding('');
      };
    }

    window.addEventListener('resize', apply);
    return () => {
      window.removeEventListener('resize', apply);
      setPadding('');
    };
  }, [ref, side, active]);
}

export default useBodyOffset;
